import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Wrench, Home } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useIntent, type Audience } from "@/intent/IntentContext";

type Card = {
  id: Audience;
  Icon: typeof Building2;
  title: string;
  desc: string;
  cta: string;
  path: string;
  tone: "mint" | "butter" | "lavender";
};

const AudienceSelector = () => {
  const { lang } = useLang();
  const { audience, setAudience } = useIntent();
  const navigate = useNavigate();
  const isEn = lang === "en";

  const cards: Card[] = [
    {
      id: "broker",
      Icon: Building2,
      title: isEn ? "I'm an estate agent" : "Ik ben makelaar",
      desc: isEn
        ? "Launch your own branded marketplace. Your vendors, your margin. Free."
        : "Start je eigen marketplace onder jouw merk. Jouw leveranciers, jouw marge. Gratis.",
      cta: isEn ? "See agent solution" : "Bekijk voor makelaars",
      path: isEn ? "/en/for-agents" : "/voor-makelaars",
      tone: "mint",
    },
    {
      id: "vendor",
      Icon: Wrench,
      title: isEn ? "I'm a vendor" : "Ik ben leverancier",
      desc: isEn
        ? "Reach buyers via agent marketplaces in your area. Pay 6% only on a sale."
        : "Bereik klanten via marketplaces van makelaars in jouw regio. Betaal 6% bij verkoop.",
      cta: isEn ? "See vendor solution" : "Bekijk voor leveranciers",
      path: isEn ? "/en/for-vendors" : "/voor-leveranciers",
      tone: "butter",
    },
    {
      id: "homeowner",
      Icon: Home,
      title: isEn ? "I'm a buyer or owner" : "Ik ben koper of eigenaar",
      desc: isEn
        ? "You use Tribeo through your agent, under their brand. See what the platform does for your home."
        : "Je gebruikt Tribeo via je makelaar, onder hun eigen merk. Bekijk wat het platform voor jouw woning doet.",
      cta: isEn ? "See what it does" : "Bekijk wat het doet",
      path: isEn ? "/en/home-advisor" : "/wooncoach",
      tone: "lavender",
    },
  ];

  const toneClass: Record<Card["tone"], string> = {
    mint: "bg-mint/40 hover:bg-mint/60",
    butter: "bg-butter/40 hover:bg-butter/60",
    lavender: "bg-lavender/40 hover:bg-lavender/60",
  };

  const handleClick = (c: Card) => {
    setAudience(c.id);
    navigate(c.path);
    window.scrollTo({ top: 0 });
  };

  return (
    <section
      aria-labelledby="audience-selector-heading"
      className="relative py-16 md:py-24 bg-cream"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-10 left-1/4 h-72 w-72 rounded-full bg-mint/40 blur-3xl" />
        <div className="absolute top-20 right-1/4 h-64 w-64 rounded-full bg-coral/30 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-lavender/40 blur-3xl" />
      </div>

      <div className="container relative">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">
            {isEn ? "Choose your path" : "Kies wat bij jou past"}
          </span>
          <h2 id="audience-selector-heading" className="mt-3 text-3xl md:text-5xl font-extrabold leading-[1.05]">
            {isEn ? "Tell us who you are." : "Vertel ons wie je bent."}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {isEn
              ? "We'll show you the parts of Tribeo that matter most to you."
              : "We laten je direct zien wat Tribeo voor jou betekent."}
          </p>
        </div>

        <ul className="mt-10 grid gap-5 md:grid-cols-3">
          {cards.map((c) => {
            const isActive = audience === c.id;
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => handleClick(c)}
                  aria-pressed={isActive}
                  className={`group relative w-full text-left rounded-3xl glass shadow-card p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-elegant ${
                    isActive ? "ring-2 ring-primary" : ""
                  }`}
                >
                  <div className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${toneClass[c.tone]} transition-colors`}>
                    <c.Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 text-xl md:text-2xl font-extrabold leading-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{c.desc}</p>
                  <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                    {c.cta}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        {audience && (
          <div className="mt-6 text-sm text-muted-foreground">
            {isEn ? "Not you?" : "Toch niet jij?"}{" "}
            <button
              type="button"
              onClick={() => setAudience(null, "selector_reset")}
              className="underline font-bold hover:text-foreground"
            >
              {isEn ? "Reset" : "Reset keuze"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AudienceSelector;
